// White-label brand for the web dashboard.
//
// Fetches the active tenant's brand from the backend (GET /api/brand) and applies
// it at runtime: document title + theme color. Same image/build serves every
// tenant; the backend it talks to decides the brand. Falls back silently to the
// static defaults baked into index.html / index.css if the call fails.

import { apiUrl } from "./apiBase";

export type Brand = {
  app_name: string;
  currency: string;
  locale: string;
  support_email: string;
  region_label: string;
  time_zone: string;
  vat_rate: number;
  primary_color: string;
  logo_url: string;
};

const DEFAULT_BRAND: Brand = {
  app_name: "Besiktning Pro",
  currency: "SEK",
  locale: "sv",
  support_email: "",
  region_label: "kommun",
  time_zone: "Europe/Stockholm",
  vat_rate: 25,
  primary_color: "#1D2D50",
  logo_url: "",
};

let current: Brand = DEFAULT_BRAND;

export function getBrand(): Brand {
  return current;
}

/** Convert "#RRGGBB" to the "H S% L%" triple Tailwind expects in --primary. */
function hexToHslTriple(hex: string): string | null {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  const int = parseInt(m[1], 16);
  const r = ((int >> 16) & 255) / 255;
  const g = ((int >> 8) & 255) / 255;
  const b = (int & 255) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      default: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

function applyToDom(brand: Brand) {
  document.title = `${brand.app_name} — Dashboard`;
  document.documentElement.lang = brand.locale || "en";
  const triple = hexToHslTriple(brand.primary_color);
  if (triple) {
    const root = document.documentElement;
    root.style.setProperty("--primary", triple);
    root.style.setProperty("--ring", triple);
  }
}

/** Fetch + apply brand. Call once on app boot, before/while rendering. */
export async function applyBrand(): Promise<Brand> {
  try {
    const res = await fetch(apiUrl("/brand"));
    if (res.ok) {
      current = { ...DEFAULT_BRAND, ...(await res.json()) };
    }
  } catch {
    // keep defaults — never block the dashboard on branding
  }
  applyToDom(current);
  return current;
}
